import { useState, useEffect, useCallback } from "react";
import { saveDailyTimeCard } from "../Api/DailyTimeCard";
import {
  fetchProjectBaseByOrg,
  fetchProjectBaseByOrgAndMember,
} from "../Api/ViewProjects";
import {
  getTasksByProject,
  getTasksByAssignedTo,
  getAllTaskStatus,
  getContactGuid,
} from "../Api/TaskMaster";
import { Container, Form, Button, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Sample14 from "../Components/Logo";
import SearchableSelect from "../Pages/ProjectComponents/SearchableSelect";
import { useAuth } from "../Context/AuthContext"; // Import useAuth

const DailyTimeCard = () => {
  const navigate = useNavigate();
  const { user } = useAuth(); // Use auth context
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [userContactGuid, setUserContactGuid] = useState(null);
  const [taskStatusMap, setTaskStatusMap] = useState({});
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [organisationId, setOrganisationId] = useState(null);
  const [timeCardData, setTimeCardData] = useState({
    UserName: "",
    Date: "",
    ProjectCode: "",
    Hours: "",
    Tasks: "",
    Remarks: "",
    Location: "",
  });

  // Get user details from localStorage with validation
  const getUserDetails = () => {
    const userDetailsRaw = localStorage.getItem("userDetails");
    if (!userDetailsRaw) {
      return null;
    }
    try {
      return JSON.parse(userDetailsRaw);
    } catch (error) {
      console.error("Error parsing userDetails:", error);
      return null;
    }
  };

  // Define admin roles
  const ADMIN_ROLES = ["Admin", "Project_Manager", "Project_Commercial"];

  const hasAdminRole = (roles) => {
    if (!roles || !Array.isArray(roles)) return false;
    return roles.some((role) => ADMIN_ROLES.includes(role));
  };

  const hasViewPermission = (roles) => {
    if (!roles || !Array.isArray(roles)) return false;
    return roles.some((role) =>
      ["Admin", "Project_Manager", "Project_Commercial"].includes(role),
    );
  };

  // Session check helper
  // const checkSession = () => {
  //   const userDetails = getUserDetails();
  //   const username = localStorage.getItem("username");

  //   if (!userDetails || !username) {
  //     toast.error("User session expired. Please login again.");
  //     navigate("/");
  //     return false;
  //   }
  //   return true;
  // };

  const checkSession = () => {
    const userDetails = getUserDetails();

    if (!userDetails) {
      toast.error("User session expired. Please login again.");
      navigate("/");
      return false;
    }

    return true;
  };

  const loadAllTaskStatuses = async () => {
    if (!checkSession()) return;

    try {
      const statuses = await getAllTaskStatus();
      const statusMap = {};
      statuses.forEach((status) => {
        statusMap[status.statusGuid.toLowerCase()] = status.statusName;
      });
      setTaskStatusMap(statusMap);
    } catch (error) {
      console.error("Error loading task statuses:", error);
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        navigate("/");
      }
    }
  };

  const getTaskStatusName = (statusGuid) => {
    if (!statusGuid) return "No Status";
    const lowerGuid = statusGuid.toLowerCase();
    return taskStatusMap[lowerGuid] || statusGuid;
  };

  const fetchUserContactGuid = async (orgId, userId) => {
    if (!checkSession()) return null;

    try {
      const response = await getContactGuid(orgId, userId);
      const contactGuid = response.contactGuid;
      setUserContactGuid(contactGuid);
      console.log("✅ Fetched Contact GUID:", contactGuid);
      return contactGuid;
    } catch (error) {
      console.error("Error fetching user contact GUID:", error);
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        navigate("/");
        return null;
      }
      if (error.response?.status === 404) {
        toast.warning(
          "Contact information API not available. Task access may be limited.",
        );
      } else {
        toast.error("Unable to fetch user contact information");
      }
      return null;
    }
  };

  const loadProjectTasks = useCallback(
    async (projectId, contactGuid, userRoles) => {
      if (!projectId) {
        setTasks([]);
        return;
      }

      if (!checkSession()) return;

      setTasksLoading(true);
      try {
        let tasksData = [];

        if (hasViewPermission(userRoles)) {
          console.log(
            "Admin user - fetching ALL tasks for project:",
            projectId,
          );
          tasksData = await getTasksByProject(projectId);
        } else {
          if (!contactGuid) {
            console.warn("No Contact GUID available for user");
            toast.warning(
              "Contact information not available. Please contact support.",
            );
            setTasks([]);
            return;
          }

          console.log(
            "Regular user - fetching tasks assigned to Contact GUID:",
            contactGuid,
          );
          const assignedTasks = await getTasksByAssignedTo(contactGuid);

          tasksData = assignedTasks.filter(
            (task) =>
              task.projectBelong === projectId ||
              task.projectBelong === parseInt(projectId),
          );

          console.log(
            `Loaded ${tasksData.length} tasks for project ${projectId}`,
          );
        }

        const mappedTasks = tasksData.map((task) => ({
          taskId: task.taskId,
          taskName: task.taskName,
          taskStatus: getTaskStatusName(task.taskStatus),
          taskStartDate: task.taskStartDate,
          taskEndDate: task.taskEndDate,
          displayValue: `${task.taskId} - ${task.taskName}`,
          fullTask: task,
        }));

        setTasks(mappedTasks);

        if (mappedTasks.length === 0 && !tasksLoading) {
          if (hasViewPermission(userRoles)) {
            toast.info("No tasks found for this project.");
          } else {
            toast.info(
              "You don't have any tasks assigned to you in this project.",
            );
          }
        }
      } catch (error) {
        console.error("Error loading tasks:", error);
        if (error.response?.status === 401) {
          toast.error("Session expired. Please login again.");
          navigate("/");
        } else {
          toast.error("Failed to load tasks for selected project");
        }
        setTasks([]);
      } finally {
        setTasksLoading(false);
      }
    },
    [taskStatusMap],
  );

  const dropdownOptions = {
    locations: [
      { value: "01", label: "01 - Work from Home" },
      { value: "02", label: "02 - Work from Office" },
      { value: "03", label: "03 - Work from Client" },
    ],
  };

  useEffect(() => {
    const fetchData = async () => {
      // Validate session first
      // const userDetails = getUserDetails();
      // const username = localStorage.getItem("username");

      // if (!userDetails || !username) {
      //   toast.error("User session expired. Please login again.");
      //   navigate("/");
      //   setInitialLoad(false);
      //   return;
      // }

      const userDetails = getUserDetails();

      if (!userDetails) {
        toast.error("User session expired. Please login again.");
        navigate("/");
        setInitialLoad(false);
        return;
      }

      try {
        const userRoles = userDetails?.roles || [];

        console.log("User Details:", userDetails);
        console.log("User Roles:", userRoles);

        const orgId =
          userDetails?.organisationId || localStorage.getItem("orgId");
        const memberId =
          userDetails?.userId || localStorage.getItem("memberId");

        if (!orgId || !memberId) {
          console.error("Missing orgId or memberId:", { orgId, memberId });
          toast.error("User information incomplete. Please login again.");
          navigate("/");
          return;
        }

        setOrganisationId(parseInt(orgId));
        console.log("✅ Organisation ID set to:", orgId);
        setUserRole(userRoles.join(", "));

        await loadAllTaskStatuses();
        const contactGuid = await fetchUserContactGuid(orgId, memberId);

        let projectsData;
        const isAdmin = hasAdminRole(userRoles);

        try {
          if (isAdmin) {
            console.log("Admin user - fetching ALL projects for org:", orgId);
            projectsData = await fetchProjectBaseByOrg(orgId);
          } else {
            console.log(
              "Regular user - fetching assigned projects for member:",
              memberId,
            );
            projectsData = await fetchProjectBaseByOrgAndMember(
              orgId,
              memberId,
            );
          }
        } catch (error) {
          console.error("Error fetching projects:", error);
          if (error.response?.status === 401) {
            toast.error("Session expired. Please login again.");
            navigate("/");
            return;
          }
          projectsData = [];
        }

        const mappedProjects = (projectsData || [])
          .filter((project) => project.active === 1)
          .map((project) => ({
            projectId: project.project_id || project.projectId,
            prTitle: project.pr_Title || project.prTitle,
            ...project,
          }));

        setProjects(mappedProjects);

        setTimeCardData((prev) => ({
          ...prev,
          UserName: userDetails.username,
          Date: new Date().toISOString().split("T")[0],
        }));
      } catch (error) {
        console.error("Error loading data:", error);
        if (error.response?.status === 401) {
          toast.error("Session expired. Please login again.");
          navigate("/");
        } else {
          toast.error(error.message || "Failed to load data");
        }
      } finally {
        setInitialLoad(false);
      }
    };

    fetchData();
  }, [navigate]);

  useEffect(() => {
    const loadTasks = async () => {
      if (timeCardData.ProjectCode) {
        const userDetails = getUserDetails();
        if (!userDetails) {
          toast.error("Session expired. Please login again.");
          navigate("/");
          return;
        }

        const userRoles = userDetails?.roles || [];
        await loadProjectTasks(
          timeCardData.ProjectCode,
          userContactGuid,
          userRoles,
        );

        setTimeCardData((prev) => ({
          ...prev,
          Tasks: "",
        }));
        setSelectedTaskId(null);
      } else {
        setTasks([]);
        setSelectedTaskId(null);
      }
    };

    loadTasks();
  }, [timeCardData.ProjectCode, loadProjectTasks, userContactGuid, navigate]);

  const handleProjectChange = (projectCode) => {
    setTimeCardData((prev) => ({
      ...prev,
      ProjectCode: projectCode,
      Tasks: "",
    }));
  };

  const handleTaskChange = (taskId) => {
    setTimeCardData((prev) => ({
      ...prev,
      Tasks: taskId,
    }));
    setSelectedTaskId(taskId);

    const selectedTask = tasks.find((task) => task.taskId === taskId);
    if (selectedTask) {
      console.log("Selected Task Details:", selectedTask);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTimeCardData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate session before submission
    if (!checkSession()) return;

    if (
      !timeCardData.Hours ||
      timeCardData.Hours <= 0 ||
      timeCardData.Hours > 24
    ) {
      toast.error("Please enter valid hours (0-24)");
      return;
    }

    if (!timeCardData.ProjectCode) {
      toast.error("Please select a project");
      return;
    }

    if (!timeCardData.Tasks) {
      toast.error("Please select a task");
      return;
    }

    if (!timeCardData.Remarks) {
      toast.error("Please enter remarks");
      return;
    }

    if (!timeCardData.Location) {
      toast.error("Please select a location");
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...timeCardData,
        OrganisationId: organisationId,
        TaskId: selectedTaskId,
      };

      console.log("Submitting time card with data:", submitData);
      await saveDailyTimeCard(submitData, timeCardData.UserName);
      toast.success("Daily Time Card submitted successfully!");

      setTimeCardData((prev) => ({
        ...prev,
        ProjectCode: "",
        Hours: "",
        Tasks: "",
        Remarks: "",
        Location: "",
      }));
      setSelectedTaskId(null);
    } catch (error) {
      console.error("Submission error:", error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error("Session expired. Please login again.");
        localStorage.removeItem("userDetails");
        localStorage.removeItem("username");
        navigate("/");
      } else if (error.response?.status === 404) {
        toast.error(
          "Time card submission API not available. Please contact support.",
        );
      } else {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to submit time card",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  if (initialLoad) {
    return (
      <div className="main-content expand">
        <Container
          fluid
          className="d-flex justify-content-center align-items-center"
          style={{ height: "80vh" }}
        >
          <Sample14 />
        </Container>
      </div>
    );
  }

  const userDetails = getUserDetails();
  const userRoles = userDetails?.roles || [];
  const isAdmin = hasAdminRole(userRoles);

  const projectOptions = projects.map((project) => ({
    id: project.projectId,
    name: `${project.projectId} - ${project.prTitle}`,
  }));

  const taskOptions = tasks.map((task) => ({
    id: task.taskId,
    name: task.displayValue,
    status: task.taskStatus,
  }));

  return (
    <>
      <div className="main-content expand">
        <Container fluid className="fields-container">
          <h2 className="fields-title">Daily Time Card</h2>

          {organisationId && (
            <div className="alert alert-secondary mb-3 small">
              <i className="fas fa-building me-2"></i>
              <strong>Organisation ID:</strong> {organisationId}
            </div>
          )}

          <div className="alert alert-info mb-3 small">
            <i className="fas fa-info-circle me-2"></i>
            {isAdmin ? (
              <span>
                <strong>Admin Access:</strong> You can see and log time for ALL
                tasks in the selected project.
              </span>
            ) : (
              <span>
                <strong>Limited Access:</strong> You can only see and log time
                for tasks assigned to you in the selected project.
              </span>
            )}
          </div>

          <Form onSubmit={handleSubmit} className="fields-form">
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    User Name <span className="text-danger">**</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="UserName"
                    value={timeCardData.UserName}
                    readOnly
                    className="read-only-field bg-light"
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Date <span className="text-danger">**</span>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    name="Date"
                    value={timeCardData.Date}
                    onChange={handleChange}
                    required
                    max={new Date().toISOString().split("T")[0]}
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Project Code <span className="text-danger">**</span>
                  </Form.Label>
                  <SearchableSelect
                    value={timeCardData.ProjectCode}
                    onChange={handleProjectChange}
                    options={projectOptions}
                    placeholder="Select Project"
                    disabled={projects.length === 0}
                    loading={initialLoad}
                    optionLabelKey="name"
                    optionValueKey="id"
                    searchPlaceholder="Search project..."
                    noResultsText="No projects found"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Task <span className="text-danger">**</span>
                  </Form.Label>
                  <SearchableSelect
                    value={timeCardData.Tasks}
                    onChange={handleTaskChange}
                    options={taskOptions}
                    placeholder={
                      !timeCardData.ProjectCode
                        ? "Select a project first"
                        : tasksLoading
                          ? "Loading tasks..."
                          : tasks.length === 0
                            ? "No tasks available"
                            : "Select Task"
                    }
                    disabled={
                      !timeCardData.ProjectCode ||
                      tasks.length === 0 ||
                      tasksLoading
                    }
                    loading={tasksLoading}
                    optionLabelKey="name"
                    optionValueKey="id"
                    searchPlaceholder="Search task..."
                    noResultsText={
                      !timeCardData.ProjectCode
                        ? "Select a project first"
                        : tasksLoading
                          ? "Loading tasks..."
                          : "No tasks found"
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Hours <span className="text-danger">**</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="Hours"
                    value={timeCardData.Hours}
                    onChange={handleChange}
                    required
                    step="0.5"
                    min="0.5"
                    max="24"
                    placeholder="Enter hours (0.5 - 24)"
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Remarks <span className="text-danger">**</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={1}
                    name="Remarks"
                    value={timeCardData.Remarks}
                    onChange={handleChange}
                    required
                    placeholder="Describe the work done..."
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Location <span className="text-danger">**</span>
                  </Form.Label>
                  <Form.Select
                    name="Location"
                    value={timeCardData.Location}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Location</option>
                    {dropdownOptions.locations.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <div className="fields-btn-container mt-4">
              <Button
                variant="primary"
                type="submit"
                className="fields-submit"
                disabled={
                  loading || projects.length === 0 || tasks.length === 0
                }
              >
                {loading ? "Submitting..." : "Submit Time Card"}
              </Button>
            </div>
          </Form>
        </Container>
      </div>

      {loading && (
        <div className="loading-overlay">
          <Sample14 />
        </div>
      )}
    </>
  );
};

export default DailyTimeCard;

// import { useState, useEffect, useCallback } from "react";
// import { saveDailyTimeCard } from "../Api/DailyTimeCard";
// import {
//   fetchProjectBaseByOrg,
//   fetchProjectBaseByOrgAndMember,
// } from "../Api/ViewProjects";
// import {
//   getTasksByProject,
//   getTasksByAssignedTo,
//   getAllTaskStatus,
//   getContactGuid,
// } from "../Api/TaskMaster";
// import { Container, Form, Button, Row, Col } from "react-bootstrap";
// import { toast } from "react-toastify";
// import { useNavigate } from "react-router-dom";
// import Sample14 from "../Components/Logo";
// import SearchableSelect from "../Pages/ProjectComponents/SearchableSelect";

// const DailyTimeCard = () => {
//   const navigate = useNavigate();
//   const [loading, setLoading] = useState(false);
//   const [initialLoad, setInitialLoad] = useState(true);
//   const [projects, setProjects] = useState([]);
//   const [tasks, setTasks] = useState([]);
//   const [tasksLoading, setTasksLoading] = useState(false);
//   const [userRole, setUserRole] = useState(null);
//   const [userContactGuid, setUserContactGuid] = useState(null);
//   const [taskStatusMap, setTaskStatusMap] = useState({});
//   const [selectedTaskId, setSelectedTaskId] = useState(null);
//   const [organisationId, setOrganisationId] = useState(null); // ADDED: Store organisation ID
//   const [timeCardData, setTimeCardData] = useState({
//     UserName: "",
//     Date: "",
//     ProjectCode: "",
//     Hours: "",
//     Tasks: "",
//     Remarks: "",
//     Location: "",
//   });

//   // Define admin roles that can see all projects and tasks
//   const ADMIN_ROLES = ["Admin", "Project_Manager", "Project_Commercial"];

//   // Check if user has admin role
//   const hasAdminRole = (roles) => {
//     if (!roles || !Array.isArray(roles)) return false;
//     return roles.some((role) => ADMIN_ROLES.includes(role));
//   };

//   // Check if user has view permission for all tasks
//   const hasViewPermission = (roles) => {
//     if (!roles || !Array.isArray(roles)) return false;
//     return roles.some((role) =>
//       ["Admin", "Project_Manager", "Project_Commercial"].includes(role),
//     );
//   };

//   // Load all task statuses for display
//   const loadAllTaskStatuses = async () => {
//     try {
//       const statuses = await getAllTaskStatus();
//       const statusMap = {};
//       statuses.forEach((status) => {
//         statusMap[status.statusGuid.toLowerCase()] = status.statusName;
//       });
//       setTaskStatusMap(statusMap);
//     } catch (error) {
//       console.error("Error loading task statuses:", error);
//     }
//   };

//   // Get task status display name
//   const getTaskStatusName = (statusGuid) => {
//     if (!statusGuid) return "No Status";
//     const lowerGuid = statusGuid.toLowerCase();
//     return taskStatusMap[lowerGuid] || statusGuid;
//   };

//   // Fetch user's contact GUID (to be used for getTasksByAssignedTo)
//   const fetchUserContactGuid = async (orgId, userId) => {
//     try {
//       const response = await getContactGuid(orgId, userId);
//       const contactGuid = response.contactGuid;
//       setUserContactGuid(contactGuid);
//       console.log("✅ Fetched Contact GUID:", contactGuid);
//       return contactGuid;
//     } catch (error) {
//       console.error("Error fetching user contact GUID:", error);
//       toast.error("Unable to fetch user contact information");
//       return null;
//     }
//   };

//   // Load tasks for selected project based on user role using new API
//   const loadProjectTasks = useCallback(
//     async (projectId, contactGuid, userRoles) => {
//       if (!projectId) {
//         setTasks([]);
//         return;
//       }

//       setTasksLoading(true);
//       try {
//         let tasksData = [];

//         if (hasViewPermission(userRoles)) {
//           // Admin/Manager/Commercial: Get ALL tasks for the project
//           console.log(
//             "Admin user - fetching ALL tasks for project:",
//             projectId,
//           );
//           tasksData = await getTasksByProject(projectId);
//         } else {
//           // Regular users: Get tasks using Contact GUID
//           if (!contactGuid) {
//             console.warn("No Contact GUID available for user");
//             toast.warning(
//               "Contact information not available. Please contact support.",
//             );
//             setTasks([]);
//             return;
//           }

//           console.log(
//             "Regular user - fetching tasks assigned to Contact GUID:",
//             contactGuid,
//           );

//           // Fetch tasks directly assigned to this user's Contact GUID
//           const assignedTasks = await getTasksByAssignedTo(contactGuid);
//           console.log(
//             `User has ${assignedTasks.length} assigned tasks total`,
//             assignedTasks,
//           );

//           // Filter tasks by projectBelong
//           tasksData = assignedTasks.filter(
//             (task) =>
//               task.projectBelong === projectId ||
//               task.projectBelong === parseInt(projectId),
//           );

//           console.log(
//             `Loaded ${tasksData.length} tasks for project ${projectId} (filtered from ${assignedTasks.length} total assigned tasks)`,
//           );

//           // Log the first task to verify structure
//           if (tasksData.length > 0) {
//             console.log("Sample task from filtered data:", tasksData[0]);
//           }
//         }

//         // Map tasks for dropdown display
//         const mappedTasks = tasksData.map((task) => ({
//           taskId: task.taskId,
//           taskName: task.taskName,
//           taskStatus: getTaskStatusName(task.taskStatus),
//           taskStartDate: task.taskStartDate,
//           taskEndDate: task.taskEndDate,
//           displayValue: `${task.taskId} - ${task.taskName}`,
//           fullTask: task,
//         }));

//         setTasks(mappedTasks);
//         console.log(`Set ${mappedTasks.length} tasks in state for dropdown`);

//         if (mappedTasks.length === 0) {
//           if (hasViewPermission(userRoles)) {
//             toast.info("No tasks found for this project.");
//           } else {
//             toast.info(
//               "You don't have any tasks assigned to you in this project.",
//             );
//           }
//         }
//       } catch (error) {
//         console.error("Error loading tasks:", error);
//         toast.error("Failed to load tasks for selected project");
//         setTasks([]);
//       } finally {
//         setTasksLoading(false);
//       }
//     },
//     [],
//   );

//   const dropdownOptions = {
//     locations: [
//       { value: "01", label: "01 - Work from Home" },
//       { value: "02", label: "02 - Work from Office" },
//       { value: "03", label: "03 - Work from Client" },
//     ],
//   };

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         // Fetch user data
//         const username = localStorage.getItem("username");
//         const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//         const userRoles = userDetails?.roles || [];

//         console.log("User Details:", userDetails);
//         console.log("User Roles:", userRoles);

//         if (!username) {
//           toast.error("User session expired. Please login again.");
//           navigate("/");
//           return;
//         }

//         // Get orgId and memberId
//         const orgId =
//           userDetails?.organisationId || localStorage.getItem("orgId");
//         const memberId =
//           userDetails?.userId || localStorage.getItem("memberId");

//         // STORE ORGANISATION ID for use in submission
//         if (orgId) {
//           setOrganisationId(parseInt(orgId));
//           console.log("✅ Organisation ID set to:", orgId);
//         }

//         // Set user role for display
//         setUserRole(userRoles.join(", "));

//         // Load task statuses
//         await loadAllTaskStatuses();

//         // Fetch user's Contact GUID (required for getTasksByAssignedTo)
//         const contactGuid = await fetchUserContactGuid(orgId, memberId);

//         let projectsData;
//         let isAdmin = hasAdminRole(userRoles);

//         // Decide which API to call based on user role
//         if (isAdmin) {
//           console.log("Admin user - fetching ALL projects for org:", orgId);
//           projectsData = await fetchProjectBaseByOrg(orgId);
//         } else {
//           console.log(
//             "Regular user - fetching assigned projects for member:",
//             memberId,
//           );
//           projectsData = await fetchProjectBaseByOrgAndMember(orgId, memberId);
//         }

//         // Filter active projects
//         const mappedProjects = (projectsData || [])
//           .filter((project) => project.active === 1)
//           .map((project) => ({
//             projectId: project.project_id || project.projectId,
//             prTitle: project.pr_Title || project.prTitle,
//             ...project,
//           }));

//         console.log("Active projects:", mappedProjects);

//         if (mappedProjects.length === 0) {
//           if (isAdmin) {
//             toast.info("No active projects found in the organization.");
//           } else {
//             toast.info("You are not assigned to any active projects.");
//           }
//         }

//         setProjects(mappedProjects);

//         // Set initial form data
//         setTimeCardData((prev) => ({
//           ...prev,
//           UserName: username,
//           Date: new Date().toISOString().split("T")[0],
//         }));
//       } catch (error) {
//         console.error("Error loading data:", error);
//         toast.error(error.message || "Failed to load data");
//       } finally {
//         setInitialLoad(false);
//       }
//     };

//     fetchData();
//   }, [navigate]);

//   // Load tasks when project changes
//   useEffect(() => {
//     const loadTasks = async () => {
//       if (timeCardData.ProjectCode) {
//         const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//         const userRoles = userDetails?.roles || [];

//         // Pass Contact GUID to load tasks
//         await loadProjectTasks(
//           timeCardData.ProjectCode,
//           userContactGuid,
//           userRoles,
//         );

//         // Reset task selection when project changes
//         setTimeCardData((prev) => ({
//           ...prev,
//           Tasks: "",
//         }));
//         setSelectedTaskId(null);
//       } else {
//         setTasks([]);
//         setSelectedTaskId(null);
//       }
//     };

//     loadTasks();
//   }, [timeCardData.ProjectCode, loadProjectTasks, userContactGuid]);

//   const handleProjectChange = (projectCode) => {
//     setTimeCardData((prev) => ({
//       ...prev,
//       ProjectCode: projectCode,
//       Tasks: "", // Reset task when project changes
//     }));
//   };

//   const handleTaskChange = (taskId) => {
//     setTimeCardData((prev) => ({
//       ...prev,
//       Tasks: taskId,
//     }));
//     setSelectedTaskId(taskId);

//     // Find and log the selected task details
//     const selectedTask = tasks.find((task) => task.taskId === taskId);
//     if (selectedTask) {
//       console.log("Selected Task Details:", selectedTask);
//     }
//   };

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setTimeCardData((prev) => ({
//       ...prev,
//       [name]: value,
//     }));
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (
//       !timeCardData.Hours ||
//       timeCardData.Hours <= 0 ||
//       timeCardData.Hours > 24
//     ) {
//       toast.error("Please enter valid hours (0-24)");
//       return;
//     }

//     setLoading(true);
//     try {
//       // Prepare data with organisation ID
//       const submitData = {
//         ...timeCardData,
//         OrganisationId: organisationId, // ADDED: Include organisation ID
//       };

//       await saveDailyTimeCard(submitData, timeCardData.UserName);
//       toast.success("Daily Time Card submitted successfully!");

//       console.log("Submitted with Task ID:", selectedTaskId);
//       console.log("Submitted with Organisation ID:", organisationId);

//       // Reset form except UserName and Date
//       setTimeCardData((prev) => ({
//         ...prev,
//         ProjectCode: "",
//         Hours: "",
//         Tasks: "",
//         Remarks: "",
//         Location: "",
//       }));
//       setSelectedTaskId(null);
//     } catch (error) {
//       console.error("Submission error:", error);
//       toast.error(error.message || "Failed to submit time card");
//     } finally {
//       setLoading(false);
//     }
//   };

//   if (initialLoad) {
//     return (
//       <div className="main-content expand">
//         <Container
//           fluid
//           className="d-flex justify-content-center align-items-center"
//           style={{ height: "80vh" }}
//         >
//           <Sample14 />
//         </Container>
//       </div>
//     );
//   }

//   // Get role display info
//   const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//   const userRoles = userDetails?.roles || [];
//   const isAdmin = hasAdminRole(userRoles);

//   // Format projects for SearchableSelect
//   const projectOptions = projects.map((project) => ({
//     id: project.projectId,
//     name: `${project.projectId} - ${project.prTitle}`,
//   }));

//   // Format tasks for SearchableSelect
//   const taskOptions = tasks.map((task) => ({
//     id: task.taskId,
//     name: task.displayValue,
//     status: task.taskStatus,
//   }));

//   return (
//     <>
//       <div className="main-content expand">
//         <Container fluid className="fields-container">
//           <h2 className="fields-title">Daily Time Card</h2>

//           {/* Organisation Info Banner - ADDED */}
//           {organisationId && (
//             <div className="alert alert-secondary mb-3 small">
//               <i className="fas fa-building me-2"></i>
//               <strong>Organisation ID:</strong> {organisationId}
//             </div>
//           )}

//           {/* Role info banner */}
//           <div className="alert alert-info mb-3 small">
//             <i className="fas fa-info-circle me-2"></i>
//             {isAdmin ? (
//               <span>
//                 <strong>Admin Access:</strong> You can see and log time for ALL
//                 tasks in the selected project.
//               </span>
//             ) : (
//               <span>
//                 <strong>Limited Access:</strong> You can only see and log time
//                 for tasks assigned to you in the selected project.
//               </span>
//             )}
//           </div>

//           <Form onSubmit={handleSubmit} className="fields-form">
//             <Row>
//               <Col md={4}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>
//                     User Name <span className="text-danger">**</span>
//                   </Form.Label>
//                   <Form.Control
//                     type="text"
//                     name="UserName"
//                     value={timeCardData.UserName}
//                     readOnly
//                     className="read-only-field bg-light"
//                   />
//                 </Form.Group>
//               </Col>

//               <Col md={4}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>
//                     Date <span className="text-danger">**</span>
//                   </Form.Label>
//                   <Form.Control
//                     type="date"
//                     name="Date"
//                     value={timeCardData.Date}
//                     onChange={handleChange}
//                     required
//                     max={new Date().toISOString().split("T")[0]}
//                   />
//                 </Form.Group>
//               </Col>

//               <Col md={4}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>
//                     Project Code <span className="text-danger">**</span>
//                   </Form.Label>
//                   <SearchableSelect
//                     value={timeCardData.ProjectCode}
//                     onChange={handleProjectChange}
//                     options={projectOptions}
//                     placeholder="Select Project"
//                     disabled={projects.length === 0}
//                     loading={initialLoad}
//                     optionLabelKey="name"
//                     optionValueKey="id"
//                     searchPlaceholder="Search project..."
//                     noResultsText="No projects found"
//                   />
//                   {projects.length === 0 && (
//                     <Form.Text className="text-warning">
//                       {isAdmin
//                         ? "No active projects found in the organization."
//                         : "You are not assigned to any active projects. Please contact your administrator."}
//                     </Form.Text>
//                   )}
//                 </Form.Group>
//               </Col>
//             </Row>

//             <Row>
//               <Col md={4}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>
//                     Task <span className="text-danger">**</span>
//                   </Form.Label>
//                   <SearchableSelect
//                     value={timeCardData.Tasks}
//                     onChange={handleTaskChange}
//                     options={taskOptions}
//                     placeholder={
//                       !timeCardData.ProjectCode
//                         ? "Select a project first"
//                         : tasksLoading
//                           ? "Loading tasks..."
//                           : tasks.length === 0
//                             ? "No tasks available"
//                             : "Select Task"
//                     }
//                     disabled={
//                       !timeCardData.ProjectCode ||
//                       tasks.length === 0 ||
//                       tasksLoading
//                     }
//                     loading={tasksLoading}
//                     optionLabelKey="name"
//                     optionValueKey="id"
//                     searchPlaceholder="Search task..."
//                     noResultsText={
//                       !timeCardData.ProjectCode
//                         ? "Select a project first"
//                         : tasksLoading
//                           ? "Loading tasks..."
//                           : "No tasks found"
//                     }
//                   />
//                   {timeCardData.ProjectCode &&
//                     tasks.length === 0 &&
//                     !tasksLoading && (
//                       <Form.Text className="text-warning">
//                         {isAdmin
//                           ? "No tasks found for this project."
//                           : "You don't have any tasks assigned to you in this project."}
//                       </Form.Text>
//                     )}
//                   {!isAdmin && timeCardData.ProjectCode && tasks.length > 0 && (
//                     <Form.Text
//                       className="text-muted"
//                       style={{ fontSize: "11px" }}
//                     >
//                       <i className="fas fa-info-circle me-1"></i>
//                       Showing only tasks assigned to you.
//                     </Form.Text>
//                   )}
//                 </Form.Group>
//               </Col>

//               <Col md={4}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>
//                     Hours <span className="text-danger">**</span>
//                   </Form.Label>
//                   <Form.Control
//                     type="number"
//                     name="Hours"
//                     value={timeCardData.Hours}
//                     onChange={handleChange}
//                     required
//                     step="0.5"
//                     min="0.5"
//                     max="24"
//                   />
//                 </Form.Group>
//               </Col>

//               <Col md={4}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>
//                     Remarks <span className="text-danger">**</span>
//                   </Form.Label>
//                   <Form.Control
//                     as="textarea"
//                     rows={1}
//                     name="Remarks"
//                     value={timeCardData.Remarks}
//                     onChange={handleChange}
//                     required
//                     placeholder="Describe the work done..."
//                   />
//                 </Form.Group>
//               </Col>
//             </Row>

//             <Row>
//               <Col md={4}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>
//                     Location <span className="text-danger">**</span>
//                   </Form.Label>
//                   <Form.Select
//                     name="Location"
//                     value={timeCardData.Location}
//                     onChange={handleChange}
//                     required
//                   >
//                     <option value="">Select Location</option>
//                     {dropdownOptions.locations.map((option) => (
//                       <option key={option.value} value={option.value}>
//                         {option.label}
//                       </option>
//                     ))}
//                   </Form.Select>
//                 </Form.Group>
//               </Col>
//             </Row>

//             <div className="fields-btn-container mt-4">
//               <Button
//                 variant="primary"
//                 type="submit"
//                 className="fields-submit"
//                 disabled={
//                   loading || projects.length === 0 || tasks.length === 0
//                 }
//               >
//                 {loading ? "Submitting..." : "Submit Time Card"}
//               </Button>
//             </div>
//           </Form>
//         </Container>
//       </div>

//       {loading && (
//         <div className="loading-overlay">
//           <Sample14 />
//         </div>
//       )}
//     </>
//   );
// };

// export default DailyTimeCard;
